import type { FirebaseClient, User } from '@akpl/firebase/client';
import { createContext, type ReactNode, use, useEffect, useMemo, useState } from 'react';

import { analytics } from '../analytics';
import { api } from '../api';
import { normalizeEmail } from './validation';

export type AuthStatus = 'loading' | 'signed_out' | 'anonymous' | 'signed_in';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoUrl: string | null;
  isAnonymous: boolean;
  emailVerified: boolean;
}

export interface AuthApi {
  status: AuthStatus;
  user: AuthUser | null;
  /** null in "offline auth" mode (`/config` without a Firebase project). */
  client: FirebaseClient | null;
  /** True when a real (non-anonymous) account is signed in. */
  isAccount: boolean;
  signIn(email: string, password: string): Promise<AuthUser>;
  signUp(input: { email: string; password: string; name: string }): Promise<AuthUser>;
  /** Guests (docs/05 §3). Reuses the persisted anonymous user when there is one. */
  signInAnonymously(): Promise<AuthUser>;
  /** Anonymous → account upgrade that keeps the uid (docs/05 §3). */
  linkAccount(input: { email: string; password: string; name?: string }): Promise<AuthUser>;
  sendPasswordReset(email: string): Promise<void>;
  /** `POST /me/logout` (best effort) then Firebase `signOut()` (docs/05 §4). */
  signOut(): Promise<void>;
  /**
   * Re-reads the Firebase user. `onAuthStateChanged` only fires when the uid changes, so it stays
   * silent after `linkAccount` (same uid, no longer anonymous) and after `updateProfile`.
   */
  syncFromClient(): void;
}

const AuthContext = createContext<AuthApi | null>(null);

function toAuthUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoUrl: user.photoURL,
    isAnonymous: user.isAnonymous,
    emailVerified: user.emailVerified,
  };
}

function statusOf(user: AuthUser | null): AuthStatus {
  if (!user) return 'signed_out';
  return user.isAnonymous ? 'anonymous' : 'signed_in';
}

export interface AuthProviderProps {
  client: FirebaseClient | null;
  children: ReactNode;
}

/**
 * Owns the Firebase Auth session for the whole app. Phase 1 providers are email/password and
 * anonymous only (decision 2026-09-08) — there is no social entry point to wire here.
 *
 * `onAuthState` is the single source of truth: every method returns the user it produced, but
 * the context state is only ever written from the listener, so a token refresh or a sign-out
 * from another tab converges to the same value.
 */
export function AuthProvider({ client, children }: AuthProviderProps) {
  const initial = useMemo<AuthUser | null>(() => {
    const current = client?.currentUser() ?? null;
    return current ? toAuthUser(current) : null;
  }, [client]);
  const [user, setUser] = useState<AuthUser | null>(initial);
  // Boot already awaited `waitForAuth()`, so with a client the first value is final; without
  // one there is nothing to wait for.
  const [status, setStatus] = useState<AuthStatus>(() => statusOf(initial));

  useEffect(() => {
    if (!client) return;
    return client.onAuthState((next) => {
      const mapped = next ? toAuthUser(next) : null;
      setUser(mapped);
      setStatus(statusOf(mapped));
      if (mapped && !mapped.isAnonymous) analytics.identify(mapped.uid);
    });
  }, [client]);

  const value = useMemo<AuthApi>(() => {
    function required(): FirebaseClient {
      if (!client) throw new Error('auth_unavailable');
      return client;
    }
    return {
      status,
      user,
      client,
      isAccount: Boolean(user && !user.isAnonymous),
      async signIn(email, password) {
        const next = await required().signInWithEmail(normalizeEmail(email), password);
        analytics.identify(next.uid);
        analytics.track('login', { method: 'password' });
        return toAuthUser(next);
      },
      async signUp({ email, password, name }) {
        const next = await required().createUserWithEmail(
          normalizeEmail(email),
          password,
          name.trim(),
        );
        analytics.identify(next.uid);
        analytics.track('login', { method: 'signup' });
        return toAuthUser(next);
      },
      async signInAnonymously() {
        const existing = client?.currentUser();
        if (existing) return toAuthUser(existing);
        const next = await required().signInAnonymously();
        return toAuthUser(next);
      },
      async linkAccount({ email, password, name }) {
        const fb = required();
        const next = await fb.linkEmailPassword(normalizeEmail(email), password);
        if (name?.trim()) await fb.updateDisplayName(name.trim());
        analytics.identify(next.uid);
        analytics.track('login', { method: 'link_anonymous' });
        return toAuthUser(next);
      },
      async sendPasswordReset(email) {
        await required().sendPasswordReset(normalizeEmail(email));
      },
      async signOut() {
        // Best effort: the API closes the hosted session, but a failing call must never trap
        // the user in a signed-in shell.
        try {
          await api.meLogout();
        } catch {
          /* offline or already expired */
        }
        analytics.track('logout', {});
        analytics.reset();
        await client?.signOut();
      },
      syncFromClient() {
        const current = client?.currentUser() ?? null;
        const mapped = current ? toAuthUser(current) : null;
        setUser(mapped);
        setStatus(statusOf(mapped));
      },
    };
  }, [client, status, user]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthApi {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
